import {Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {Subscription} from 'rxjs';
import {SpinnerService} from '../../../services/spinner.service';
import {CardService} from '../../../services/card.service';
import {HttpResponse} from '@angular/common/http';
import {CustomHttpResponse} from '../../../model/custom-http-response';
import {NotificationService} from '../../../notification/notification.service';
import {NotificationType} from '../../../enum/notification-type.enum';
import {MatDialogRef} from '@angular/material/dialog';
import {AnalyticsUserService} from '../../../services/analytics-user.service';

@Component({
  selector: 'app-link-card',
  templateUrl: './link-card.component.html',
  styleUrl: './link-card.component.scss',
})
export class LinkCardComponent implements OnInit, OnDestroy {
  id!: string | undefined;
  code!: string | undefined;
  form!: UntypedFormGroup;
  subscription: Subscription[] = [];
  data!: any;
  isSubmitting = false;
  hasError = false;
  hasSuccess = false;
  otpControls = [1, 2, 3, 4, 5, 6];

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  constructor(
    private route: ActivatedRoute,
    private spinnerService: SpinnerService,
    private cardService: CardService,
    private notificationService: NotificationService,
    private router: Router,
    private dialogRef: MatDialogRef<LinkCardComponent>,
    private analyticsUserService: AnalyticsUserService
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      this.code = params['code'];
      this.id = params['id'];
    });

    this.createForm();

    this.subscription.push(
      this.form.valueChanges.subscribe((value) => {
        const isComplete = this.isOtpComplete();

        if (isComplete && !this.isSubmitting) {
          this.isSubmitting = true;
          this.save();
        }
      })
    );
  }

  ngOnDestroy() {
    console.log('[LinkCard] ngOnDestroy – unsubscribing');
    this.subscription.forEach((s) => s.unsubscribe());
  }

  private createForm() {
    this.form = new UntypedFormGroup({
      key1: new UntypedFormControl(''),
      key2: new UntypedFormControl(''),
      key3: new UntypedFormControl(''),
      key4: new UntypedFormControl(''),
      key5: new UntypedFormControl(''),
      key6: new UntypedFormControl(''),
    });
  }

  private isOtpComplete(): boolean {
    const values = this.form.value;

    return Object.values(values).every((v) => typeof v === 'string' && v.length === 1);
  }


  save() {
    const rawValues = this.form.value;

    const key = Object.values(rawValues)
      .map((v) => (v ?? '').toString().toUpperCase())
      .join('');

    if (key.length !== 6) {
      this.isSubmitting = false;
      return;
    }

    this.spinnerService.show();

    this.subscription.push(
      this.cardService.linkCard(this.code ?? '', key).subscribe({
        next: (response) => {
          this.spinnerService.hide();

          this.hasSuccess = true;
          this.notificationService.sendNotification(
            NotificationType.SUCCESS,
            response.body?.message || '',
            ''
          );

          setTimeout(() => {
            this.router.navigateByUrl(`/cards/${this.id}/details-card-partner/update`);
            this.dialogRef.close();
          }, 600);
          this.getAnalyticsCard(true);
        },
        error: (error) => {
          this.spinnerService.hide();
          this.isSubmitting = false;
          this.hasError = true;
          this.notificationService.errorHandler(error);
        },
      })
    );
  }

  onInput(event: Event, index: number) {
    this.hasError = false;
    this.hasSuccess = false;
    this.hasSuccess = false;
    const input = event.target as HTMLInputElement;
    const value = input.value.toUpperCase();

    input.value = value;

    const controlName = `key${index + 1}`;
    this.form.get(controlName)?.setValue(value, {emitEvent: false});

    if (!/^[A-Z0-9]$/.test(value)) {
      input.value = '';
      this.form.get(controlName)?.setValue('', {emitEvent: false});
      return;
    }

    if (value && index < this.otpInputs.length - 1) {
      console.log('[LinkCard] Moving focus to next input');
      this.otpInputs.toArray()[index + 1].nativeElement.focus();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace') {
      const input = event.target as HTMLInputElement;
      if (!input.value && index > 0) {
        console.log('[LinkCard] Backspace → moving focus back');
        this.otpInputs.toArray()[index - 1].nativeElement.focus();
      }
    }
  }

  getAnalyticsCard(isTrue: boolean) {
    this.spinnerService.show();

    this.subscription.push(
      this.analyticsUserService.getAnalyticsCardUser(isTrue, null).subscribe({
        next: (response: HttpResponse<CustomHttpResponse>) => {
          this.spinnerService.hide();
          this.data = response.body?.data;
        },
        error: (error) => {
          this.spinnerService.hide();
        },
      })
    );
  }
}
